import { colors } from '@ardoq/design-tokens';
import { ButtonColorMap, ButtonType } from './types';
import { buttonShadow } from './shadows';

const TERTIARY_BUTTON_COLOR_MAP: ButtonColorMap = {
  default: {
    color: colors.textDefault,
    backgroundColor: colors.surfaceActionTertiary,
    iconColor: colors.icon,
    border: `1px solid ${colors.borderAction}`,
    boxShadow: buttonShadow('tertiaryDefault'),
  },
  hover: {
    backgroundColor: colors.surfaceActionTertiaryHover,
    border: `1px solid ${colors.borderAction}`,
    boxShadow: buttonShadow('tertiaryHover'),
  },
  active: {
    backgroundColor: colors.surfaceActionTertiaryPressed,
    iconColor: colors.iconStrong,
    border: `1px solid ${colors.borderAction}`,
    boxShadow: buttonShadow('tertiaryActive'),
  },
  disabled: {
    backgroundColor: colors.surfaceActionTertiaryDisabled,
    color: colors.textDisabled,
    iconColor: colors.iconActionDisabled,
    border: `1px solid ${colors.borderDisabled}`,
    boxShadow: buttonShadow('tertiaryDisabled'),
  },
};

const GHOST_BUTTON_COLOR_MAP: ButtonColorMap = {
  default: {
    color: colors.textDefault,
    backgroundColor: colors.surfaceActionGhost,
    iconColor: colors.icon,
  },
  hover: {
    backgroundColor: colors.surfaceActionGhostHover,
  },
  active: {
    color: colors.textModerate,
    backgroundColor: colors.surfaceActionGhostPressed,
    iconColor: colors.iconActionHover,
  },
  // selected: {
  //   color: colors.textAction,
  //   iconColor: colors.iconActionActive,
  //   backgroundColor: colors.surfaceActionGhostPressed,
  // },
  disabled: {
    backgroundColor: colors.surfaceActionGhostDisabled,
    color: colors.textDisabled,
    iconColor: colors.iconActionDisabled,
  },
};

const SECONDARY_BUTTON_COLOR_MAP: ButtonColorMap = {
  default: {
    color: colors.textBrand,
    iconColor: colors.textBrand,
    backgroundColor: colors.surfaceActionSecondary,
    border: `1px solid ${colors.brand200}`,
    boxShadow: buttonShadow('secondaryDefault'),
  },
  hover: {
    backgroundColor: colors.surfaceActionSecondaryHover,
    border: `1px solid ${colors.brand200}`,
    boxShadow: buttonShadow('secondaryHover'),
  },
  active: {
    backgroundColor: colors.surfaceActionSecondaryPressed,
    border: `1px solid ${colors.brand200}`,
    boxShadow: buttonShadow('secondaryActive'),
  },
  disabled: {
    backgroundColor: colors.surfaceActionSecondaryDisabled,
    color: colors.textDisabled,
    iconColor: colors.iconActionDisabled,
    border: `1px solid ${colors.borderDisabled}`,
    boxShadow: 'none',
  },
};

const PRIMARY_BUTTON_COLOR_MAP: ButtonColorMap = {
  default: {
    color: colors.textOnInvertedStrong,
    backgroundColor: colors.surfaceActionPrimary,
    boxShadow: buttonShadow('primaryDefault'),
    border: `1px solid ${colors.brand900}`,
  },
  hover: {
    backgroundColor: colors.surfaceActionPrimaryHover,
    boxShadow: buttonShadow('primaryHover'),
  },
  active: {
    backgroundColor: colors.surfaceActionPrimaryPressed,
    boxShadow: buttonShadow('primaryActive'),
  },
  disabled: {
    backgroundColor: colors.surfaceDisabled,
    color: colors.textDisabled,
    border: `1px solid ${colors.borderDisabled}`,
    boxShadow: 'none',
  },
};

const DANGER_BUTTON_COLOR_MAP: ButtonColorMap = {
  default: {
    color: colors.textOnInvertedStrong,
    backgroundColor: colors.surfaceActionDelete,
    border: `1px solid ${colors.red700}`,
    boxShadow: buttonShadow('dangerDefault'),
  },
  hover: {
    backgroundColor: colors.surfaceActionDeleteHover,
    boxShadow: buttonShadow('dangerHover'),
  },
  active: {
    backgroundColor: colors.surfaceActionDeletePressed,
    boxShadow: buttonShadow('dangerActive'),
  },
  disabled: {
    backgroundColor: colors.surfaceDisabled,
    boxShadow: 'none',
    color: colors.textDisabled,
    border: `1px solid ${colors.borderDisabled}`,
  },
};

const SECONDARY_DANGER_BUTTON_COLOR_MAP: ButtonColorMap = {
  default: {
    color: colors.textError,
    backgroundColor: colors.surfaceActionTertiary,
    iconColor: colors.iconError,
    border: `1px solid ${colors.borderError}`,
    boxShadow: buttonShadow('secondaryDangerDefault'),
  },
  hover: {
    backgroundColor: colors.surfaceActionTertiaryHover,
    border: `1px solid ${colors.borderError}`,
    boxShadow: buttonShadow('secondaryDangerHover'),
  },
  active: {
    backgroundColor: colors.surfaceActionTertiaryPressed,
    iconColor: colors.iconError,
    border: `1px solid ${colors.borderError}`,
    boxShadow: buttonShadow('secondaryDangerActive'),
  },
  disabled: {
    backgroundColor: colors.surfaceActionTertiaryDisabled,
    color: colors.textDisabled,
    iconColor: colors.iconActionDisabled,
    border: `1px solid ${colors.borderDisabled}`,
    boxShadow: buttonShadow('secondaryDangerDisabled'),
  },
};

const AI_BUTTON_COLOR_MAP: ButtonColorMap = {
  default: {
    color: colors.textDefault,
    backgroundColor: '#FFF',
    border: `1px solid transparent`,
    boxShadow: 'none',
  },
  hover: {
    border: `1px solid transparent`,
    backgroundColor: '#FFF',
    boxShadow: `0 0 0 2px rgba(255, 255, 255, 0.16) inset, 0 -2px 2px 0 rgba(255, 255, 255, 0.20) inset, 0 2px 1px 0 rgba(226, 194, 255, 0.15)`,
  },
  active: {
    backgroundColor: '#FFF',
    border: `1px solid transparent`,
    boxShadow: `0 0 0 2px rgba(255, 255, 255, 0.16) inset, 0 -2px 2px 0 rgba(255, 255, 255, 0.20) inset, 0 2px 1px 0 rgba(226, 194, 255, 0.15)`,
  },
  disabled: {
    background: colors.surfaceActionTertiaryDisabled,
    boxShadow: 'none',
    color: colors.textDisabled,
    border: `1px solid ${colors.borderActionDisabled}`,
  },
};

const INPUT_LOOK_BUTTON_COLOR_MAP: ButtonColorMap = {
  default: {
    backgroundColor: colors.surfaceActionGhost,
    boxShadow: `0px 0px 0px 1px ${colors.borderAction}`,
  },
  hover: {
    boxShadow: `0px 0px 0px 1px ${colors.borderActionHover}`,
  },
  active: {
    boxShadow: `0px 0px 0px 2px ${colors.borderActionActive}`,
  },
  disabled: {
    backgroundColor: colors.surfaceDisabled,
  },
};

export const BUTTON_COLOR_MAP: Record<ButtonType, ButtonColorMap> = {
  [ButtonType.SECONDARY]: SECONDARY_BUTTON_COLOR_MAP,
  [ButtonType.PRIMARY]: PRIMARY_BUTTON_COLOR_MAP,
  [ButtonType.DANGER]: DANGER_BUTTON_COLOR_MAP,
  [ButtonType.SECONDARY_DANGER]: SECONDARY_DANGER_BUTTON_COLOR_MAP,
  [ButtonType.GHOST]: GHOST_BUTTON_COLOR_MAP,
  [ButtonType.TERTIARY]: TERTIARY_BUTTON_COLOR_MAP,
  [ButtonType.AI]: AI_BUTTON_COLOR_MAP,
  [ButtonType.INPUT_LOOK]: INPUT_LOOK_BUTTON_COLOR_MAP,
};
